<template>
    <PageSection fixed>
        <PageHeader title="Students" />
        <Card>
            <TableLayout>
                <template #toolbar>
                    <div class="table-toolbar b-0">
                        <SearchBar placeholder="Search name or email" @search="fetch({ query: $event })" />
						<button v-if="csvDownloadEnabled" class="btn-header color-gray-1000"
							:class="[downloading && 'opacity-50 cursor-not-allowed']"
							:disabled="downloading" @click="downloadCSV">
							<LoadingSpinner v-if="downloading && downloadProgress === 0" />
							<svg v-else-if="downloading" width="24" height="24" viewBox="0 0 24 24"
								fill="none" class="download-progress">
								<circle cx="12" cy="12" r="10" stroke-width="4"
									stroke="currentColor" fill="transparent" opacity="0.2" />
								<circle cx="12" cy="12" r="10" stroke-width="4"
									stroke="currentColor" fill="transparent"
									:stroke-dasharray="`${downloadProgress * 0.6283} 62.83`"
									stroke-linecap="round" transform="rotate(-90 12 12)" />
							</svg>
							<DownloadIcon v-else class="scale-75" />
							<span class="pl-[0.25rem] font-semibold text-sm">
                                Download
                            </span>
						</button>
						<FilterSet :filters="filterableColumns" @filters-updated="handleUpdatedFilters"
                                :initialFilters="initialFilters" />
						<DateFilterComponent :selectedDateAfter="selectedDateAfter"
							:selectedDateRange="selectedDateRange" v-model:multi-calendars="isMultiCalendars"
							@update:selectedDateAfter="handleDateRangeChanged"
							@update:selectedDateRange="handleDateRangeChanged" />
						<ColumnVisibility :table="table" />
                    </div>
                </template>
                <Table :table="table" :show-loading-spinner="loading" aria-describedBy="page-header" class="b-l-style-none b-r-style-none b-b-style-solid">
                    <thead class="table-thead">
                        <tr class="flex" v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                            <TableHeaderCell v-for="header in headerGroup.headers" :header="header">
                            </TableHeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="table-row" v-for="row in table.getCoreRowModel().rows" :key="row.id">
                            <TableDataCell v-for="cell in row.getVisibleCells()" :cell="cell">
                                <template #collected_print>
                                    <Tag :variant="getPrintStatusChipType(cell.row.original)" :clearable="false">
                                        {{ getPrintStatusText(cell.row.original) }}
                                        <template #tooltip v-if="getAllStatusChips(cell).length > 0">
                                            <div v-for="state in getAllStatusChips(cell)"
                                                class="min-w-[200px] flex-row m-1">
                                                <span class="px-2">{{ state.count }} {{ state.count > 1 ? 'books' : 'book'
                                                }}</span>
                                                <Tag :variant="getPrintStatusChipType({
                                                    booklist_ids: cell.row.original.booklist_ids,
                                                    collected_print: state.status
                                                } as EnrolmentRow)" :clearable="false">
                                                    {{ getPrintStatusText({
                                                        booklist_ids: cell.row.original.booklist_ids,
                                                        collected_print: state.status
                                                    } as EnrolmentRow) }}
                                                </Tag>
                                            </div>
                                        </template>
                                    </Tag>
                                </template>
                                <template #status>
                                    <Tag :variant="getStatusChipType(cell.row.original, printCollectionEnabled)"
                                        :clearable="false">
                                        {{ getStatusText(cell.row.original, printCollectionEnabled) }}
                                    </Tag>
                                </template>
                                <template #actions>
                                    <Menu placement="auto" :items="getRowActions(cell)" class="cell-menu" is-icon />
                                </template>
                            </TableDataCell>
                        </tr>
                    </tbody>
                    <template #empty>
                        <EmptyState :icon="message.icon" :title="message.title" :text="message.description">
                        </EmptyState>
                    </template>
                </Table>
                <template #paginator>
                    <Paginator :table="table" :page-sizes="[5, 10, 25, 50, 100, 200]" class="b-0"/>
                </template>
            </TableLayout>
        </Card>
        <CollectPrintModal :open="state.matches('CollectingPrint')" :request="state.context.collectPrintAction[0]"
            :submitting="state.matches({ CollectingPrint: 'Submitting' })" @close="cancel"
            @confirm="confirmPrintCollection" />
        <OptInOptOutModal :isPrintCollectionEnabled="printCollectionEnabled" :open="state.matches('UpdatingOptOut')"
            :request="state.context.optAction[0]" :submitting="state.matches({ UpdatingOptOut: 'Submitting' })"
            @close="cancel" @confirm="confirmOptOut" />
        <SetAllStatusModal v-if="selectedUser" :open="isSettingAllStatus" @close="cancel"
            @confirm="(state) => selectedUser && setAllStatus(selectedUser, state)" :loading="isSubmitting"
            :enrolment="selectedUser" :books="enrolledBooks" />
        <PrintManagementModal v-if="isManagingStatus && selectedUser" :books="enrolledBooks" @cancel="cancel"
            @setStatus="updateStatus" @setAllStatus="() => selectedUser && openUpdateAllStatusModal(selectedUser)"
            :isFetching="isFetchingBooks" :selectedUser="selectedUser" :isSaving="isSubmittingChanges"
            :statusUpdates="statusUpdates" @confirm="confirmUpdateStatuses" @discard="discard"
            :isUpdated="isStateUpdated" />
    </PageSection>
</template>

<script lang="ts" setup>
import {
	Cell,
	functionalUpdate,
	getCoreRowModel,
	useVueTable,
} from "@tanstack/vue-table";
import { EnrolmentFilterValuesInput } from "core-services/stacks/institutions/substacks/enrolment/lib/schemas/opt-out-status";
import { PrintCollection } from "core-services/stacks/institutions/substacks/enrolment/lib/schemas/print-collection";
import { definePage } from "vue-router/auto";
import ColumnVisibility from "../../../../shared/components/ComposableTable/ColumnVisibility.vue";
import Table from "../../../../shared/components/ComposableTable/Table.vue";
import TableDataCell from "../../../../shared/components/ComposableTable/TableDataCell.vue";
import TableHeaderCell from "../../../../shared/components/ComposableTable/TableHeaderCell.vue";
import TableLayout from "../../../../shared/components/ComposableTable/TableLayout.vue";
import { useGuardedTableColumns } from "../../../../shared/components/ComposableTable/useGuardedTableColumns";
import {
	Filter,
	SelectedFilter,
} from "../../../../shared/components/Filters/Filter.vue";
import FilterSet from "../../../../shared/components/Filters/FilterSet.vue";
import SearchBar from "../../../../shared/components/Headers/SearchBar.vue";
import LoadingSpinner from "../../../../shared/components/LoadingSpinner.vue";
import { MenuItemData } from "../../../../shared/components/Menu.vue";
import PageSection from "../../../../shared/components/PageSection.vue";
import { useFeatureFlag } from "../../../../shared/composables/useFeatureFlag";
import { useTableFilters } from "../../../../shared/composables/useTableFilters";
import DownloadIcon from "../../assets/icons/download.svg?component";
import DateFilterComponent from "../../components/Filter/DateFilterComponent.vue";
import PageHeader from "../../components/PageHeader.vue";
import CollectPrintModal from "../../components/Users/CollectPrintModal.vue";
import OptInOptOutModal from "../../components/Users/OptInOptOutModal.vue";
import PrintManagementModal from "../../components/Users/PrintManagementModal.vue";
import SetAllStatusModal from "../../components/Users/SetAllStatusModal.vue";
import { useToast } from "../../composables/useToast";
import { useUserManagementTable } from "../../composables/useUserManagementTable";
import {
	EnrolmentRow,
	StrictEnrolmentRow,
} from "../../state-machines/users-table-actor";
import { useAcademicDates } from "../../stores/academic-dates";
import { trpc } from "../../trpc/client";
import { formattedDate } from "../../trpc/utils/transformDate";
import { generateTableColumns } from "../../utils/booklist-table-columns";
import { transformPathToTerm } from "../../utils/path-to-term";
import {
	getPrintStatusChipType,
	getPrintStatusText,
	getStatusChipType,
	getStatusText,
} from "../../utils/print-collection-status";

const academicDatesStore = useAcademicDates();

const isMultiCalendars = ref<boolean>(true);
const selectedDateAfter = ref<Date>(new Date());
const selectedDateRange = ref<Date[]>(
	academicDatesStore.selectedDates
		? [
				new Date(academicDatesStore.selectedDates?.startDate),
				new Date(academicDatesStore.selectedDates?.endDate),
			]
		: [],
);

const {
	cancel,
	confirmPrintCollection,
	confirmOptOut,
	state,
	rows: data,
	selectedUser,
	isFetchingBooks,
	isSubmittingChanges,
	isManagingStatus,
	enrolledBooks,
	totalRowCount,
	pagination,
	loading,
	filters,
	columnPinning,
	columns: userTableColumns,
	fetch,
	message,
	updateOptOutStatus,
	updateMultiplePrintStatus,
	updatePrintStatus,
	printCollectionEnabled,
	isSettingAllStatus,
	isSubmitting,
	openUpdateAllStatusModal,
	discard,
	updateStatus,
	statusUpdates,
	confirmUpdateStatuses,
	isStateUpdated,
} = useUserManagementTable();

const { initialFilters, saveFiltersToStorage, parseFiltersToQuery } =
	useTableFilters({
		localStorageKey: "studentsFilters",
		parsingTransformations: {
			// Map column IDs to backend filter field names
			external_user_id: "external_id",
			path: "path",
		},
	});

watch(
	() => academicDatesStore.selectedDates,
	(newValue, oldValue) => {
		if (newValue?.id === oldValue?.id) {
			return;
		}

		fetch({
			rowsPerPage: Number(window.localStorage?.preferredTablePageSize ?? 25),
			page: 1,
			filters: {
				...filters.value,
				path:
					academicDatesStore.selectedDates &&
					academicDatesStore.selectedDates.id !== "all_dates"
						? [academicDatesStore.selectedDates.id]
						: undefined,
			},
		});

		// update date picker and switch to multi calendars
		isMultiCalendars.value = true;
		selectedDateRange.value = academicDatesStore.selectedDates
			? [
					new Date(academicDatesStore.selectedDates?.startDate),
					new Date(academicDatesStore.selectedDates?.endDate),
				]
			: [];
	},
);

const columns = useGuardedTableColumns<EnrolmentRow>((helper) => [
	...generateTableColumns(userTableColumns.value, helper),
	{
		columnDef: helper.display({
			id: "actions",
			header: "Actions",
			size: 72,
			enableSorting: false,
			enableHiding: false,
			enableResizing: false,
		}),
	},
]);

const table = useVueTable<EnrolmentRow>({
	getCoreRowModel: getCoreRowModel<EnrolmentRow>(),
	manualPagination: true,
	columnResizeMode: "onChange",
	get data() {
		return data.value;
	},
	get columns() {
		return columns.value;
	},
	get rowCount() {
		return totalRowCount.value;
	},
	state: {
		get pagination() {
			return pagination.value;
		},
		get columnPinning() {
			return columnPinning.value;
		},
	},
	onPaginationChange(updater) {
		const newPagination = functionalUpdate(updater, pagination.value);
		fetch({
			page: newPagination.pageIndex + 1,
			rowsPerPage: newPagination.pageSize,
		});
		window.localStorage.setItem(
			"preferredTablePageSize",
			newPagination.pageSize.toString(),
		);
	},
});

function getAllStatusChips(cell: Cell<EnrolmentRow, unknown>) {
	const { allPrintCollectionStatuses } = cell.row.original;

	if (allPrintCollectionStatuses?.length === 0) {
		return [];
	}

	// Count the number of unique statuses
	const statusStats = new Map<EnrolmentRow["collected_print"], number>();
	for (const status of allPrintCollectionStatuses ?? []) {
		statusStats.set(status, (statusStats.get(status) ?? 0) + 1);
	}

	// if no statues or single status, return empty array.
	// because no partial print status tooltip is needed
	if (statusStats.size <= 1) {
		return [];
	}

	// Used to sort the statuses in the tooltip
	const printStatusPriority = {
		unprocessed: 1,
		processing: 2,
		back_order: 3,
		packed: 4,
		collected: 5,
		returned: 6,
		mixed: 7,
	};

	// return the array of statuses
	const statusStatsWithPiroity = Array.from(statusStats, ([status, count]) => ({
		status,
		count,
		priority: printStatusPriority[status],
	}));

	statusStatsWithPiroity.sort((a, b) => a.priority - b.priority);
	return statusStatsWithPiroity;
}

function getRowActions(cell: Cell<EnrolmentRow, unknown>): MenuItemData[] {
	const isKnownUser = typeof cell.row.original.internal_user_id === "number";
	const hasCollectedPrint = cell.row.original.collected_print === "collected";
	let strictEnrolment: StrictEnrolmentRow | undefined;
	const enrolment = cell.row.original;
	if (enrolment.internal_user_id !== undefined) {
		strictEnrolment = {
			...enrolment,
			internal_user_id: enrolment.internal_user_id,
			booklist_ids: enrolment.booklist_ids ?? [],
		};
	}

	const optInOutMenuItem: MenuItemData = {
		label:
			cell.row.original.opted_out && !hasCollectedPrint ? "Opt In" : "Opt Out",
		action: () => {
			if (strictEnrolment) {
				updateOptOutStatus({
					enrolment: strictEnrolment,
					optingOut: !strictEnrolment.opted_out,
				});
			}
		},
		disabled: !isKnownUser,
		tooltip: {
			enabled: !isKnownUser,
			position: "left",
			content:
				"Student enrolment data is inactive/incomplete, contact ES to update",
		},
	};

	if (!printCollectionEnabled.value) {
		return [optInOutMenuItem];
	}

	const disableManagePrintCollection =
		!isKnownUser || cell.row.original.booklist_ids?.length === 0;
	const toolTipMessage =
		cell.row.original.booklist_ids?.length === 0
			? "This student has no print books."
			: "Student enrolment data is inactive/incomplete, contact ES to update";
	return [
		{
			label: "Manage Print Collection",
			disabled: disableManagePrintCollection,
			action: () => {
				if (strictEnrolment) {
					updateMultiplePrintStatus(
						cell.row.original.booklist_ids,
						strictEnrolment,
					);
				}
			},
			tooltip: {
				enabled: disableManagePrintCollection,
				position: "left",
				content: toolTipMessage,
			},
		},
		{
			label: "Set All Print Collection Statuses",
			hasDivider: true,
			action: () => {
				if (strictEnrolment) {
					openUpdateAllStatusModal(strictEnrolment);
				}
			},
			disabled: disableManagePrintCollection,
			tooltip: {
				enabled: disableManagePrintCollection,
				position: "left",
				content: toolTipMessage,
			},
		},
		optInOutMenuItem,
	];
}

function setAllStatus(
	enrolment: StrictEnrolmentRow,
	status: PrintCollection["state"],
) {
	updatePrintStatus({
		enrolment: enrolment,
		requireOptIn: !(
			["returned", "unprocessed"].includes(status) && enrolment.opted_out
		),
		state: status,
	});
}

// ---- Filtering Start ----

// Map table column IDs to backend filter field names
const columnToFilterField: Record<string, EnrolmentFilterValuesInput["field"]> =
	{
		external_user_id: "external_user_id",
		first_name: "first_name",
		last_name: "last_name",
		email: "email",
		path: "path",
	};

const filterableColumns = computed<Filter[]>(() => {
	const allowedColumns = Object.keys(columnToFilterField);
	const columnFilters: Filter[] = [];

	for (const column of columns.value) {
		const columnId = typeof column.id === "string" ? column.id : null;

		if (!columnId || !allowedColumns.includes(columnId)) {
			continue;
		}

		const filterField = columnToFilterField[columnId];

		columnFilters.push({
			id: columnId,
			label: (column.header ?? columnId) as string,
			options: {
				search: "server",
				type: "multi",
				fetch: async (query?: string) => {
					const paths =
						academicDatesStore.selectedDates &&
						academicDatesStore.selectedDates.id !== "all_dates"
							? [academicDatesStore.selectedDates.id]
							: [];
					const response = await trpc.enrolments.getFilterValues.query({
						field: filterField,
						query,
						paths,
					});
					return {
						values: response.values,
						hasMore: response.hasMore,
					};
				},
				formatLabel: (value) => {
					// For path filter, we want to show the full path instead of just the last part
					if (filterField === "path") {
						return transformPathToTerm(value);
					}
					// For other filters, we can show the value as is
					return value;
				},
			},
		});
	}
	return columnFilters;
});

async function handleUpdatedFilters(latestFilters: SelectedFilter[]) {
	const groupedFilters = parseFiltersToQuery(latestFilters);

	fetch({
		page: 1,
		startDate: academicDatesStore.selectedDates?.startDate,
		endDate: academicDatesStore.selectedDates?.endDate,
		rowsPerPage: Number(
			globalThis.window.localStorage?.preferredTablePageSize ?? 25,
		),
		filters: {
			...groupedFilters,
			path:
				academicDatesStore.selectedDates &&
				academicDatesStore.selectedDates.id !== "all_dates"
					? [academicDatesStore.selectedDates.id]
					: undefined,
		},
	});

	// Store the filters to localstorage
	saveFiltersToStorage(latestFilters);
}
// ---- Filtering End ----

// ---- Date Filter Start ----

function handleDateRangeChanged([startDate, endDate]: [Date?, Date?]) {
	fetch({
		page: 1,
		startDate: startDate?.toISOString(),
		endDate: endDate?.toISOString(),
		filters: {
			...filters.value,
		},
	});
}

// ---- Date Filter End ----

// ---- CSV Download Start ----

const csvDownloadEnabled = useFeatureFlag("students-csv-download");
const downloading = ref(false);
const downloadProgress = ref(0);
const { showErrorToast } = useToast();

async function downloadCSV() {
	downloading.value = true;
	downloadProgress.value = 0;
	try {
		const batchSize = 500;
		const maxRows = 50_000;
		const allRows: EnrolmentRow[] = [];
		const { filters: currentFilters, query: currentQuery } =
			state.value.context.fetchOptions;

		// First request to get total count
		const firstResponse = await trpc.enrolments.getInstitutionEnrolments.query({
			page: 1,
			rowsPerPage: batchSize,
			offset: 0,
			query: currentQuery,
			filters: currentFilters,
		});
		allRows.push(...firstResponse.rows);

		const totalRows = Math.min(firstResponse.metadata.count, maxRows);
		downloadProgress.value =
			totalRows === 0 ? 100 : Math.round((allRows.length / totalRows) * 100);

		if (allRows.length < totalRows) {
			const totalRequests = Math.ceil(totalRows / batchSize);
			// Fetch 10 pages concurrently to avoid combining all requests
			// into a single GET URL that exceeds the server's URI length limit (414).
			const batchConcurrency = 10;

			for (let i = 1; i < totalRequests; i += batchConcurrency) {
				const promises = [];
				for (
					let j = i;
					j < Math.min(i + batchConcurrency, totalRequests);
					j++
				) {
					promises.push(
						trpc.enrolments.getInstitutionEnrolments.query({
							page: 1,
							rowsPerPage: batchSize,
							offset: j * batchSize,
							query: currentQuery,
							filters: currentFilters,
						}),
					);
				}
				const responses = await Promise.all(promises);
				for (const response of responses) {
					allRows.push(...response.rows);
				}
				downloadProgress.value = Math.round((allRows.length / totalRows) * 100);
			}
		}

		const visibleColumns = table
			.getAllColumns()
			.filter((col) => col.getIsVisible() && col.id !== "actions");

		const headers = visibleColumns.map(
			(col) =>
				(typeof col.columnDef.header === "string"
					? col.columnDef.header
					: col.id) as string,
		);

		const csvRows = allRows.map((row) =>
			visibleColumns
				.map((col) => {
					const value = formatCellValue(col.id, row);
					return escapeCsvField(value);
				})
				.join(","),
		);

		const csv = [headers.map(escapeCsvField).join(","), ...csvRows].join(
			"\r\n",
		);
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.setAttribute("href", url);
		link.setAttribute(
			"download",
			`students-${new Date().toISOString().slice(0, 10)}.csv`,
		);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);
	} catch {
		showErrorToast("Failed to download student data. Please try again.");
	} finally {
		downloading.value = false;
	}
}

function escapeCsvField(value: string): string {
	if (value.includes(",") || value.includes('"') || value.includes("\n")) {
		return `"${value.replaceAll('"', '""')}"`;
	}
	return value;
}

function formatCellValue(columnId: string, row: EnrolmentRow): string {
	const value = row[columnId as keyof EnrolmentRow];
	switch (columnId) {
		case "path":
			return transformPathToTerm(value as string);
		case "created_at":
		case "updated_at":
			return formattedDate({
				date: value as string,
				language: globalThis.navigator.language,
			});
		case "collected_print":
			return getPrintStatusText(row);
		case "status":
			return getStatusText(row, printCollectionEnabled.value);
		default:
			return value == null ? "" : String(value);
	}
}

// ---- CSV Download End ----

definePage({
	meta: {
		requiresAuth: true,
		featureFlag: "user-management-page",
	},
});
</script>

<style scoped>
.download-progress circle {
	transition: stroke-dasharray 0.3s ease;
}
</style>
